import axios from '@/lib/axios';
import { unwrapApiResponse } from '@/lib/unwrap-api-response';
import { useQuery, type UseQueryOptions } from '@tanstack/react-query';

/** Matches `GET /compliance/bookings/{booking_id}/review-exchange/`. */
export interface BookingReviewExchangeCleaner {
  cleaner_id: string;
  cleaner_name: string;
  customer_review_submitted: boolean;
  customer_review_rating: number | null;
  customer_review_comments: string | null;
  customer_review_revealed: boolean;
  cleaner_review_submitted: boolean;
  cleaner_review_rating: number | null;
}

export interface BookingReviewExchange {
  booking_id: string;
  review_window_expires_at: string | null;
  review_window_expired: boolean;
  cleaners: BookingReviewExchangeCleaner[];
  issue_report_submitted: boolean;
  issue_report_id: string | null;
  issue_report_status: string | null;
}

export const bookingReviewExchangeQueryKey = (bookingId: string) =>
  ['compliance', 'review-exchange', bookingId] as const;

async function getBookingReviewExchange(
  bookingId: string
): Promise<BookingReviewExchange> {
  const response = await axios.get(
    `/compliance/bookings/${bookingId}/review-exchange/`
  );
  return unwrapApiResponse<BookingReviewExchange>(response.data);
}

export function useGetBookingReviewExchangeQuery(
  bookingId: string,
  options?: Omit<
    Partial<UseQueryOptions<BookingReviewExchange, Error>>,
    'queryKey' | 'queryFn'
  >
) {
  const { enabled: enabledFromOptions, ...rest } = options ?? {};
  return useQuery({
    queryKey: bookingReviewExchangeQueryKey(bookingId),
    queryFn: () => getBookingReviewExchange(bookingId),
    retry: false,
    enabled:
      !!bookingId &&
      bookingId.trim().length > 0 &&
      (enabledFromOptions ?? true),
    ...rest,
  });
}
